"use client";

import React, { useMemo, useState } from "react";
import { InView } from "react-intersection-observer";
import CountryFlag from "react-country-flag";
import { motion, AnimatePresence } from "framer-motion";
import { sbBlok } from "@/types/sb-types";
import Dropdown, { Option } from "@/components/Dropdown";
import { groupInnerVariants } from "@/components/Leaderboard/animations";
import { useDebounce } from "@/hooks/useDebounce";
import SearchIcon from "@mui/icons-material/Search";
import clsx from "clsx";

interface OOMLeaderboardProps {
  blok?: sbBlok;
  csvFiles: Array<{ name: string; data: Array<any> }>;
  isNews?: boolean;
}

const roundOptions = {
  season1: [
    { id: 1, label: "Round 1" },
    { id: 2, label: "Round 2" },
    { id: 3, label: "Round 3" },
    { id: 4, label: "Round 4" },
    { id: 5, label: "Round 5" },
    { id: 6, label: "Finale" },
  ],
  season2: [
    { id: 1, label: "Round 1" },
    { id: 2, label: "Round 2" },
    { id: 3, label: "Round 3" },
    { id: 4, label: "Round 4" },
    { id: 5, label: "Round 5" },
    { id: 6, label: "Round 6" },
  ],
  season3: [
    { id: 1, label: "Round 1" },
    { id: 2, label: "Round 2" },
    { id: 3, label: "Round 3" },
    { id: 4, label: "Finale" },
  ],
} as Record<string, { id: number; label: string }[]>;

const OOMLeaderboard = ({ blok, csvFiles, isNews }: OOMLeaderboardProps) => {
  /* isNews prop manages /news/breakdown-of-player-earnings */
  const [activeRows, setActiveRows] = useState<string[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number>(
    isNews && blok?.selected_season
      ? +blok?.selected_season
      : csvFiles?.length || 1
  );
  const [selectedRound, setSelectedRound] = useState(1);
  const [searchText, setSearchText] = useState("");

  const seasonOptions = isNews
    ? [
        { id: 1, label: "Season 1" },
        { id: 2, label: "Season 2" },
        { id: 3, label: "Season 3" },
      ]
    : csvFiles?.map((_, index) => ({
        id: index + 1,
        label: `Season ${index + 1}`,
      }));

  const dataFromCSV = isNews
    ? csvFiles?.find(
        (file) =>
          file.name.includes(`S${selectedSeason}`) &&
          file.name.includes(`R${selectedRound}`)
      )?.data
    : csvFiles?.[selectedSeason - 1]?.data;

  const handleRowClick = (id: string) => {
    setActiveRows((prevRows) =>
      prevRows.includes(id)
        ? prevRows.filter((i) => i !== id)
        : [...prevRows, id]
    );
  };

  const handleSeasonChange = (season: Option) => {
    if (season.id && season.id !== selectedSeason) {
      setSelectedSeason(+season.id);
      setSelectedRound(1);
      setActiveRows([]);
    }
  };

  const handleRoundChange = (round: Option) => {
    if (round.id && round.id !== selectedRound) {
      setSelectedRound(+round.id);
      setActiveRows([]);
    }
  };

  const debouncedSearchText = useDebounce(searchText, 300);

  const filteredData = useMemo(() => {
    if (!debouncedSearchText) return dataFromCSV?.slice(1) || []; // Return all rows if no search text
    return (
      dataFromCSV
        ?.slice(1) // Exclude the header row
        .filter((row) =>
          row.some((cell: any) =>
            cell
              ?.toString()
              .toLowerCase()
              .includes(debouncedSearchText.toLowerCase())
          )
        ) || []
    );
  }, [dataFromCSV, debouncedSearchText]);

  const handleSearchInputChange = (event: { target: { value: any } }) => {
    const searchText = event.target.value;
    setSearchText(searchText);
  };

  return (
    <div className="grid-container mx-0 lg:mx-auto">
      <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26">
        <div className="grid-container">
          <div className="col-start-1 col-span-4 lg:col-start-1 lg:col-span-16 2xl:col-start-1 2xl:col-span-32">
            {!isNews && (
              <div className="flex flex-col md:flex-row justify-center items-center gap-[10px] mx-0 mb-[10px] md:mt-14">
                <Dropdown
                  key="seasons"
                  options={seasonOptions}
                  defaultValue={seasonOptions.find(
                    (opt: { id: number; label: string }) =>
                      opt.id === selectedSeason
                  )}
                  onChange={handleSeasonChange}
                />
              </div>
            )}
            {isNews && (
              <div className="flex flex-col md:flex-row justify-center lg:justify-start items-center gap-[10px] mx-0 mb-[10px] md:mt-14">
                <Dropdown
                  key="seasons"
                  options={seasonOptions}
                  defaultValue={seasonOptions.find(
                    (opt: { id: number; label: string }) =>
                      opt.id === selectedSeason
                  )}
                  onChange={handleSeasonChange}
                />

                <Dropdown
                  key={`rounds-${selectedSeason}`}
                  options={roundOptions[`season${selectedSeason}`]}
                  defaultValue={roundOptions[`season${selectedSeason}`].find(
                    (opt: { id: number; label: string }) =>
                      opt.id === selectedRound
                  )}
                  onChange={handleRoundChange}
                />

                <div className="flex items-center w-full max-w-[330px] lg:max-w-[280px] min-h-[45px] text-[15px] bg-tm-c-gray-800 rounded-[10px] cursor-pointer px-4">
                  <input
                    type="search"
                    placeholder="Search for player"
                    value={searchText}
                    onChange={handleSearchInputChange}
                    className="custom-search-input w-full border-none bg-transparent p-0 m-0 text-[15px] focus-visible:outline-none"
                  />
                  <SearchIcon
                    fontSize="medium"
                    className="text-white cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26">
        <div
          className={clsx("pb-32 overflow-y-auto scroll-smooth", {
            "mt-0": isNews,
            "mt-16 md:mt-32": !isNews,
          })}
        >
          {dataFromCSV && dataFromCSV?.length > 0 && (
            <div
              role="table"
              className="bg-[#2c2c2c] rounded-[10px] overflow-hidden min-h-[800px]"
            >
              <div
                role="thead"
                className="flex justify-between bg-tm-c-gray-700 px-5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
              >
                {dataFromCSV[0].slice(0, 4).map((i: string, n: number) => (
                  <div
                    role="th"
                    className="flex-1 text-center py-[15px] px-0 text-xs lg:text-[15px] nth-of-type-3:flex-[4] nth-of-type-3:text-left nth-of-type-4:flex-[2] md:nth-of-type-3:flex-[5] md:nth-of-type-4:flex-1"
                    key={`${i}-${n}`}
                  >
                    {n === 1 ? <span className="hidden lg:block">{i}</span> : i}
                  </div>
                ))}
              </div>
              <div role="tbody">
                {filteredData.map((i: any, index: number) => {
                  const rowID: string =
                    i[0] + i[2]?.replace(/\s/g, "") + i[3] || i[0] + index;

                  const filteredDataFromCSV = dataFromCSV[0].filter(
                    (str: string) => !!str?.length
                  );

                  return (
                    <InView
                      key={`${rowID}-${index}`}
                      threshold={0.5}
                      triggerOnce
                    >
                      {({ inView, ref }) => (
                        <div
                          ref={ref}
                          role="tr"
                          className={`cursor-pointer transition-opacity duration-300 ease-in-out
                            border-b border-tm-c-gray-700 last-of-type:border-b-0 lg:hover:bg-[#171717]
                            flex flex-col justify-between overflow-hidden opacity-100
                          `}
                        >
                          {inView ? (
                            <div>
                              <div
                                role="tr"
                                className={clsx(
                                  "py-0 px-5 lg:p-0 flex justify-between gap-1 md:gap-2.5",
                                  {
                                    "bg-tm-c-gray-900":
                                      activeRows.includes(rowID),
                                  }
                                )}
                                onClick={() => handleRowClick(rowID)}
                              >
                                {i.slice(0, 4).map((d: any, n: number) => (
                                  <div
                                    role="td"
                                    className={`px-0 py-[15px] text-center flex-1 text-xs flex items-center justify-center relative md:text-[15px]
                                      nth-of-type-3:flex-[4] nth-of-type-3:text-left nth-of-type-3:justify-start md:nth-of-type-3:flex-[5]
                                      nth-of-type-4:flex-[2] md:nth-of-type-4:flex-1
                                      `}
                                    key={`${index}-${n}-${d}`}
                                  >
                                    {n === 1 ? (
                                      d ? (
                                        <CountryFlag
                                          countryCode={d}
                                          style={{ fontSize: "1.5em" }}
                                          svg
                                        />
                                      ) : (
                                        ""
                                      )
                                    ) : (
                                      <span>{d}</span>
                                    )}
                                  </div>
                                ))}
                              </div>

                              <AnimatePresence>
                                {activeRows.includes(rowID) && (
                                  <motion.div
                                    role="rowgroup"
                                    className={clsx(
                                      "overflow-hidden cursor-default",
                                      {
                                        "bg-tm-c-gray-900":
                                          activeRows.includes(rowID),
                                      }
                                    )}
                                    variants={groupInnerVariants}
                                    initial="initial"
                                    animate={
                                      activeRows.includes(rowID)
                                        ? "open"
                                        : "initial"
                                    }
                                    exit="initial"
                                  >
                                    <div className="bg-tm-c-gray-800 m-5 rounded-[10px] overflow-hidden">
                                      {filteredDataFromCSV.length <= 10 && (
                                        <>
                                          <div
                                            role="thead"
                                            className="flex justify-between bg-tm-c-gray-700 px-5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
                                          >
                                            {filteredDataFromCSV
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex items-center justify-center flex-1
                                                    text-center text-xs md:text-[15px] px-0 py-[15px]
                                                    `}
                                                  key={`table-header-${d}-${n}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className="flex justify-between w-full gap-[5px] md:gap-2.5 px-5 py-0 lg:p-0"
                                          >
                                            {i
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex items-center justify-center flex-1
                                                    text-center text-xs md:text-[15px] px-0 py-[15px]
                                                  `}
                                                  key={`tr-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                        </>
                                      )}

                                      {/* Desktop table */}
                                      {filteredDataFromCSV.length > 10 && (
                                        <div className="hidden lg:block">
                                          <div
                                            role="thead"
                                            className="flex justify-between bg-tm-c-gray-700 px-5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
                                          >
                                            {filteredDataFromCSV
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`table-header-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className="flex justify-between w-full gap-[5px] md:gap-2.5"
                                          >
                                            {i
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`table-row-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Mobile table */}
                                      {filteredDataFromCSV.length > 10 && (
                                        <div className="block lg:hidden">
                                          <div
                                            role="thead"
                                            className="flex justify-between bg-tm-c-gray-700 px-5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
                                          >
                                            {filteredDataFromCSV
                                              .slice(4, 8)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`thead-td-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className="flex justify-between w-full gap-[5px] md:gap-2.5 px-5 py-0 lg:p-0"
                                          >
                                            {i
                                              .slice(4, 8)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`tr-td-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="thead"
                                            className="flex justify-between bg-tm-c-gray-700 px-5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
                                          >
                                            {filteredDataFromCSV
                                              .slice(8, 11)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`thead-row-cell-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className="flex justify-between w-full gap-[5px] md:gap-2.5 px-5 py-0 lg:p-0"
                                          >
                                            {i
                                              .slice(8, 11)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={`relative flex flex-1 items-center justify-center
                                                     text-center text-xs md:text-[15px] px-0 py-[15px]
                                                 `}
                                                  key={`tr-cell-${n}-${d}`}
                                                >
                                                  <span>{d}</span>
                                                </div>
                                              ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ) : (
                            <div className="py-0 px-2.5 lg:p-0 flex justify-between gap-1 md:gap-2.5"></div>
                          )}
                        </div>
                      )}
                    </InView>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OOMLeaderboard;
